import { ref, computed, type Component } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import {
    hasTaskView,
    resolveTaskView,
} from '@Components/completion/taskViews';

// Module-level singletons: dashboard mounts the sheet once and multiple
// consumers (TaskCard via useTaskNavigation) share this state. Same pattern
// as useTaskPriming.
const isOpen = ref(false);
const pendingTask = ref<TaskItem | null>(null);

export function useTaskSheet() {
    const activeView = computed<Component | null>(() =>
        pendingTask.value ? resolveTaskView(pendingTask.value) : null,
    );

    const taskHasView = (task: TaskItem): boolean => hasTaskView(task);

    const openTaskSheet = (task: TaskItem) => {
        if (!hasTaskView(task)) return;
        pendingTask.value = task;
        isOpen.value = true;
    };

    const closeSheet = () => {
        // Leave `pendingTask` populated while the sheet's exit animation
        // plays — `TaskSheet`'s `v-if="view && task"` would
        // otherwise collapse the content to blank mid-transition. The
        // next `openTaskSheet` call overwrites it.
        isOpen.value = false;
    };

    return {
        isOpen,
        pendingTask,
        activeView,
        taskHasView,
        openTaskSheet,
        closeSheet,
    };
}
